/** The header every screen wears, and the frame underneath it.
 *
 * Forty-eight pixels, the brand mark, and a row of links whose
 * membership is the clearest statement the portal makes about who you are: a
 * signed-out visitor is offered the public map and a way to watch someone
 * drive, a signed-in one is offered their own sessions, and an admin is
 * offered an inbox with a count on it. Nothing is greyed out and nothing is
 * shown-but-locked — a link that is not yours is simply not in the row.
 *
 * The rule under the header fades at both ends rather than meeting the edges,
 * which is the design system's signature and the reason it is a `div` with a
 * gradient and not a `border-bottom`.
 *
 * `Shell` is a layout route: it draws the header and renders whichever screen
 * matched. The screens bring their own `<Page>`, because the measure varies —
 * 1100 for sign-in, 1400 for sessions — and a screen knows its own better
 * than the shell does.
 */

import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { Avatar } from '@/components/ui/Avatar';
import { getOpenIssueCount, spectatePath } from '@/lib/api';
import { useMe } from '@/lib/session';
import type { Me } from '@/lib/types';

/** Placeholder links, flagged in the design handoff: the donation page has
 *  not been chosen (spec §9) and the docs live with the logger, not here. */
export const DONATE_URL = 'https://github.com/sponsors/jbhoorasingh';
export const DOCS_URL = 'https://github.com/jbhoorasingh/gt7-datalogger#readme';

/**
 * The GT7 Datalogger mark: a racing line that runs into a telemetry trace and
 * a 7. A raster from the approved icon pack (`portal/public/`), so the 64 px
 * file is offered to a high-density screen for the 32 px slot. Decorative —
 * the words beside it are the link's name.
 */
export function BrandMark() {
  return (
    <img
      className="brand-mark"
      src="/menu-icon-64.png"
      srcSet="/menu-icon-32.png 1x, /menu-icon-64.png 2x"
      width={32}
      height={32}
      alt=""
    />
  );
}

function HeartIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 21s-7.5-4.6-9.5-9.3C1 7.8 3.4 4 7 4c2 0 3.6 1.1 5 3 1.4-1.9 3-3 5-3 3.6 0 6 3.8 4.5 7.7C19.5 16.4 12 21 12 21z" />
    </svg>
  );
}

/**
 * Where the header's "Live" link points.
 *
 * The account id, not the display name. `GET /v1/live/{user}` resolves the
 * segment against `users.id`, so a link built from a name sends the driver to
 * `no such account` — about their own stream, from their own header, while
 * the spectate URL the same account was given elsewhere works. One key, used
 * everywhere: the listing links by id, `/api/me` hands out an id, and so does
 * this.
 */
export function liveHref(me: Me): string {
  return spectatePath(me.user.id);
}

export function Shell() {
  const { me, loading } = useMe();
  const { pathname } = useLocation();
  const admin = me?.user.role === 'admin';

  // Null until it is known, and null is not a zero: a badge reading 0 is a
  // claim that the inbox is empty, which is not what "the count has not
  // arrived" or "the count could not be read" means.
  const [openIssues, setOpenIssues] = useState<number | null>(null);

  useEffect(() => {
    if (!admin) {
      setOpenIssues(null);
      return;
    }
    let alive = true;
    void getOpenIssueCount()
      .then((counts) => {
        if (alive) setOpenIssues(counts.open);
      })
      .catch(() => {
        // The header is context, not content. A failure here costs the badge,
        // and the Issues screen reports its own errors in its own body.
        if (alive) setOpenIssues(null);
      });
    return () => {
      alive = false;
    };
  }, [admin]);

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <header className="nav">
        <Link className="nav-brand" to="/">
          <BrandMark />
          GT7 Sync
        </Link>

        {/* Nothing is drawn until the account is known: a signed-in visitor
            should never see the signed-out row flash past first. */}
        {loading ? null : me ? (
          <nav
            aria-label="Main"
            style={{ display: 'contents' }}
          >
            <NavLink to="/sessions">Sessions</NavLink>
            <NavLink to="/analysis">Analysis</NavLink>
            <NavLink to="/tracks">Tracks</NavLink>
            <NavLink to={liveHref(me)}>Live</NavLink>
            <a
              href={DONATE_URL}
              target="_blank"
              rel="noreferrer noopener"
              style={{
                color: 'var(--color-accent-300)',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <HeartIcon />
              Donate
            </a>
            {admin ? (
              <NavLink
                to="/admin"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
              >
                Admin
                {openIssues === null ? null : (
                  <span className="tag tag-outline" style={{ fontSize: 9, padding: '1px 6px' }}>
                    <span className="sr-only">open issues:&nbsp;</span>
                    {openIssues}
                  </span>
                )}
              </NavLink>
            ) : null}
            <NavLink
              to="/profile"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                fontSize: 12,
                color: 'var(--color-neutral-400)',
              }}
            >
              <span className="tabular-nums nav-name">{me.user.display_name}</span>
              <Avatar name={me.user.display_name} size={24} />
            </NavLink>
          </nav>
        ) : (
          <nav aria-label="Main" style={{ display: 'contents' }}>
            <NavLink to="/tracks">Tracks</NavLink>
            <NavLink to="/live">Spectate</NavLink>
            <a
              href={DONATE_URL}
              target="_blank"
              rel="noreferrer noopener"
              style={{ color: 'var(--color-accent-300)' }}
            >
              Donate
            </a>
            <a
              href={DOCS_URL}
              target="_blank"
              rel="noreferrer noopener"
              style={{ color: 'var(--color-neutral-500)' }}
            >
              Docs
            </a>
            <NavLink to="/signin" style={{ marginLeft: 4 }}>
              Sign in
            </NavLink>
          </nav>
        )}
      </header>

      <div className="rule" />

      {/* Keyed by address: a screen that threw stays thrown until the
          boundary is remounted, and leaving the page is what should do it. */}
      <ErrorBoundary key={pathname}>
        <Outlet />
      </ErrorBoundary>
    </div>
  );
}

export default Shell;
